import React, { useEffect, useRef, useState } from "react";

import { Text, VStack } from "@chakra-ui/react";

import axios from "utils/axios";

type SupersetEmbedProps = {
  dashboardId: string;
  supersetDomain: string;
  height?: string | number;
};

const SupersetEmbed: React.FC<SupersetEmbedProps> = ({
  dashboardId,
  supersetDomain,
  height = "100%",
}) => {
  const mountPointRef = useRef<HTMLDivElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [menuBarHeight, setMenuBarHeight] = useState(0);
  const [embedHeight, setEmbedHeight] = useState<number | null>(null);

  // Fit embed between its top edge and the fixed MenuBar so Superset's own
  // scroll area + filters are never covered.
  useEffect(() => {
    const update = () => {
      const el = mountPointRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setEmbedHeight(
        Math.max(400, window.innerHeight - top - menuBarHeight),
      );
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [menuBarHeight]);

  useEffect(() => {
    let observer: ResizeObserver | undefined;
    const attach = () => {
      const menuBar = document.getElementById("app-menubar");
      if (!menuBar) {
        return false;
      }
      setMenuBarHeight(menuBar.offsetHeight);
      observer = new ResizeObserver(() =>
        setMenuBarHeight(menuBar.offsetHeight),
      );
      observer.observe(menuBar);
      return true;
    };

    // MenuBar is lazy-loaded, retry until it mounts
    let timer: ReturnType<typeof setInterval> | undefined;
    if (!attach()) {
      timer = setInterval(() => {
        if (attach() && timer) {
          clearInterval(timer);
        }
      }, 200);
    }

    return () => {
      if (timer) clearInterval(timer);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const mountPoint = mountPointRef.current;

    const mountDashboard = async () => {
      if (!mountPoint) {
        return;
      }

      setErrorMessage(null);
      mountPoint.innerHTML = "";

      try {
        const { embedDashboard } = await import("@superset-ui/embedded-sdk");

        await embedDashboard({
          id: dashboardId,
          mountPoint,
          supersetDomain,
          fetchGuestToken: async () => {
            const response = await axios.get("/superset/guest-token", {
              params: {
                dashboardId,
                supersetDomain,
              },
            });

            return (
              response?.data?.data?.guestToken ??
              response?.data?.guestToken ??
              response?.data?.token
            );
          },
          dashboardUiConfig: {
            hideTitle: true,
            hideChartControls: true,
          },
          iframeSandboxExtras: [
            "allow-top-navigation",
            "allow-popups-to-escape-sandbox",
          ],
        });
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Gagal memuat dashboard Superset.",
        );
      }
    };

    mountDashboard();

    return () => {
      isMounted = false;
      if (mountPoint) {
        mountPoint.innerHTML = "";
      }
    };
  }, [dashboardId, supersetDomain]);

  if (errorMessage) {
    return (
      <Text color="red.500" textAlign="center" mt="10">
        {errorMessage}
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={0}>
      <div
        ref={mountPointRef}
        className="superset"
        style={{
          height: embedHeight ?? height,
          width: "100%",
          position: "relative",
        }}
      />
    </VStack>
  );
};

export default SupersetEmbed;

